import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Drawer from "../../../../components/Drawer/Drawer";
import { cn } from "../../../../handlers/cn.utils";
import { useUpcomingJobs } from "../../../../services/create-resume/queries";
import JobListingItem from "../../../JobsPage/components/JobListingItem";
import ViewJob from "./ViewJob";

function JobsByCategory() {
	const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
	const [selectedJob, setSelectedJob] = useState(null);

	const navigate = useNavigate();
	const [categories, setCategories] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState("All");
	const [jobs, setJobs] = useState([]);

	const { data: response, isLoading } = useUpcomingJobs();

	useEffect(() => {
		if (!isLoading && response) {
			setJobs(response.data);
		}
	}, [isLoading, response]);

	useEffect(() => {
		const categories = jobs.map((job) => job.jobExpertise);
		const uniqueCategories = [...new Set(categories)];
		setCategories(uniqueCategories);
	}, [jobs]);

	const [filteredJobs, setFilteredJobs] = useState([...jobs]);

	useEffect(() => {
		if (selectedCategory === "All") {
			setFilteredJobs(jobs);
		} else {
			const filtered = jobs.filter((job) => job.jobExpertise === selectedCategory);

			setFilteredJobs(filtered);
		}
	}, [selectedCategory, jobs]);

	const filtersContainerRef = useRef(null);

	function handleScroll(direction) {
		const container = filtersContainerRef.current;
		const scrollAmount = 200;

		if (direction === "right") {
			container.scrollLeft += scrollAmount;
		} else {
			container.scrollLeft -= scrollAmount;
		}
	}

	return (
		<section className="py-12 px-4 relative overflow-hidden">
			<Drawer
				isOpen={showJobDetailsModal}
				onClose={() => {
					setShowJobDetailsModal(false);
				}}
			>
				<ViewJob data={selectedJob} setData={setSelectedJob} setShowJobDetailsModal={setShowJobDetailsModal} />
			</Drawer>
			<div className="relative z-10 max-w-6xl mx-auto">
				<h2 className="text-3xl font-bold mb-8 text-gray-900 font-plus-jakarta">Job Listings by Category</h2>
				<div className="relative mb-8 overflow-hidden">
					<button
						className="absolute right-0 top-6 transform -translate-y-1/2 bg-white rounded-lg p-2 shadow-md z-10"
						onClick={() => handleScroll("right")}
						style={{ borderRadius: "8px 0px 0px 8px" }}
					>
						<span className="text-2xl">›</span>
					</button>

					<span
						className={cn(
							"absolute left-0 z-10 top-1 ml-8 px-6 py-3 rounded text-[14px] min-w-fit cursor-pointer text-center font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-md bg-[#fff] text-gray-700 !shadow",
							{
								"!bg-gray-900 text-[white]": selectedCategory === "All",
							},
						)}
						onClick={() => setSelectedCategory("All")}
					>
						All
					</span>

					<div className="flex overflow-x-hidden scrollbar-hide ml-28 mr-8 !pb-4" ref={filtersContainerRef}>
						<div className="flex w-full gap-3">
							{categories.map((category, index) => (
								<span
									className={cn(
										"flex-1 px-6 py-3 cursor-pointer rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-md bg-white text-gray-700 text-center text-nowrap shadow",
										{
											"!bg-gray-700 text-[white] focus:text-[white]": selectedCategory === category,
										},
									)}
									onClick={() => setSelectedCategory(category)}
									key={index}
								>
									{category}
								</span>
							))}
						</div>
					</div>
					<button
						className="absolute left-0 top-6 transform -translate-y-1/2 bg-white rounded-lg p-2 shadow-md z-10"
						onClick={() => handleScroll("left")}
						style={{ borderRadius: "0px 8px 8px 0px" }}
					>
						<span className="text-2xl">‹</span>
					</button>
				</div>
				<div className="relative overflow-hidden rounded-lg">
					<div className="absolute inset-0 bg-[linear-gradient(60deg,_#ffffff_0%,_var(--tw-color-primary-50)_50%,_#ffffff_100%)] opacity-50 backdrop-filter backdrop-blur-sm" />
					<div className="relative z-10">
						{filteredJobs.map((job, index) => (
							<JobListingItem
								key={index}
								job={{
									...job,
									logo: job.company ? job.company.logo_url : "https://via.placeholder.com/150",
									salary: null,
								}}
								showCompany={true}
								onClick={() => {
									setSelectedJob(job);
									setShowJobDetailsModal(true);
								}}
								onApply={() => {
									setSelectedJob(job);
									setShowJobDetailsModal(true);
								}}
								titleClassName="text-sm font-semibold"
							/>
						))}
					</div>
				</div>
				<div className="flex justify-between items-center py-4 border-b border-gray-200 transition-colors duration-300">
					<div className="mt-8 mx-auto">
						<button
							className="inline-block bg-[#fff] border border-gray-200 text-gray-700 px-6 py-2 rounded hover:bg-gray-50 transition-colors font-inter"
							onClick={() => {
								const params = new URLSearchParams();

								if (selectedCategory) {
									params.append("category", selectedCategory);
								}

								params.append("order", "upcoming");

								const queryString = params.toString();
								const link = `/jobs${queryString ? `?${queryString}` : ""}`;

								navigate(link);
							}}
						>
							View {selectedCategory === "All" ? "" : "All " + selectedCategory} Jobs
						</button>
					</div>
				</div>
			</div>
		</section>
	);
}

export default JobsByCategory;
